import { useState, useEffect, useCallback } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ShieldCheck, ShieldAlert, Activity, DollarSign, ScrollText, HeartPulse, Settings, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ enabled }) {
  return enabled
    ? <Badge data-testid="status-active" className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">Actif</Badge>
    : <Badge data-testid="status-inactive" variant="secondary" className="text-[11px]">Inactif</Badge>;
}

function FiletCard({ label, enabled, detail, icon: Icon, color }) {
  return (
    <div data-testid={`filet-${label.toLowerCase().replace(/\s/g, '-')}`} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
      <div className={`p-2 rounded-md ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium">{label}</span>
          <StatusBadge enabled={enabled} />
        </div>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label, open, onToggle }) {
  return (
    <button onClick={onToggle} className="flex items-center gap-2 w-full text-left py-2">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <span className="text-sm font-semibold flex-1">{label}</span>
      {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
    </button>
  );
}

function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}

export default function PlatformSecurityPage() {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [costAlerts, setCostAlerts] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [tenantHealth, setTenantHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState({ filets: true, alerts: true, audit: true, health: true });
  const [threshold, setThreshold] = useState('');
  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, caRes, alRes, thRes] = await Promise.all([
        axios.get(`${API}/platform/security/overview`, { headers }),
        axios.get(`${API}/platform/security/cost-alerts`, { headers }),
        axios.get(`${API}/platform/security/audit-log?limit=30`, { headers }),
        axios.get(`${API}/platform/security/tenant-health`, { headers }),
      ]);
      setOverview(ovRes.data);
      setCostAlerts(caRes.data);
      setAuditLog(alRes.data);
      setTenantHealth(thRes.data);
      setThreshold(String(caRes.data.default_threshold_usd));
      setAlertsEnabled(caRes.data.enabled);
    } catch (e) {
      console.error('Security fetch error:', e);
      toast.error('Erreur chargement sécurité');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSection = (key) => setSections(s => ({ ...s, [key]: !s[key] }));

  const saveAlertConfig = async () => {
    try {
      await axios.put(
        `${API}/platform/security/cost-alerts/config?enabled=${alertsEnabled}&default_threshold_usd=${threshold}`,
        null,
        { headers }
      );
      toast.success('Configuration sauvegardée');
      fetchAll();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
  };

  if (loading && !overview) {
    return (
      <PlatformLayout title="Sécurité & Gouvernance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Chargement...</div>
        </div>
      </PlatformLayout>
    );
  }

  const filets = overview?.filets || {};
  const rl = filets.rate_limiting || {};
  const alerts = costAlerts?.alerts || [];
  const exceededAlerts = alerts.filter(a => a.exceeded);
  const actions = auditLog?.actions || [];
  const healthIssues = tenantHealth?.issues || [];

  return (
    <PlatformLayout title="Sécurité & Gouvernance">
      <div className="space-y-6" data-testid="security-page">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="card-filets">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-muted-foreground uppercase">Filets actifs</span>
              </div>
              <span className="text-2xl font-bold">
                {Object.values(filets).filter(f => f?.enabled).length}
              </span>
              <span className="text-sm text-muted-foreground ml-1">/ {Object.keys(filets).length}</span>
            </CardContent>
          </Card>

          <Card data-testid="card-rate-limit">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-muted-foreground uppercase">Rate Limiter</span>
              </div>
              <span className="text-2xl font-bold">{rl.active_users || 0}</span>
              <span className="text-sm text-muted-foreground ml-1">utilisateurs actifs</span>
            </CardContent>
          </Card>

          <Card data-testid="card-cost-alerts">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className={`w-4 h-4 ${exceededAlerts.length > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                <span className="text-xs font-medium text-muted-foreground uppercase">Alertes coût</span>
              </div>
              <span className="text-2xl font-bold">{exceededAlerts.length}</span>
              <span className="text-sm text-muted-foreground ml-1">dépassement(s)</span>
            </CardContent>
          </Card>

          <Card data-testid="card-health">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <HeartPulse className={`w-4 h-4 ${healthIssues.length > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
                <span className="text-xs font-medium text-muted-foreground uppercase">Santé tenants</span>
              </div>
              <span className="text-2xl font-bold">{healthIssues.length}</span>
              <span className="text-sm text-muted-foreground ml-1">anomalie(s)</span>
            </CardContent>
          </Card>
        </div>

        {/* Section: Filets de sécurité */}
        <Card>
          <CardHeader className="pb-2">
            <SectionHeader icon={ShieldCheck} label="Filets de sécurité actifs" open={sections.filets} onToggle={() => toggleSection('filets')} />
          </CardHeader>
          {sections.filets && (
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                <FiletCard
                  label="Rate Limiting"
                  enabled={rl.enabled}
                  detail={`Strict: ${rl.strict_limit}/min (chat/voice) · Normal: ${rl.normal_limit}/min (CRUD)`}
                  icon={Activity} color="bg-blue-500"
                />
                <FiletCard
                  label="TenantGuard"
                  enabled={filets.tenant_guard?.enabled}
                  detail={filets.tenant_guard?.description || ''}
                  icon={Shield} color="bg-indigo-500"
                />
                <FiletCard
                  label="Clone Policy 2-tier"
                  enabled={filets.clone_policy?.enabled}
                  detail={`${filets.clone_policy?.global_rules_count || 0} règles globales · Tenant + Global`}
                  icon={ShieldAlert} color="bg-violet-500"
                />
                <FiletCard
                  label="Source Validator"
                  enabled={filets.source_validator?.enabled}
                  detail={`${filets.source_validator?.known_providers || 0} providers connus`}
                  icon={ShieldCheck} color="bg-teal-500"
                />
                <FiletCard
                  label="Dédup Mémoires"
                  enabled={filets.dedup_memories?.enabled}
                  detail={filets.dedup_memories?.description || ''}
                  icon={ShieldCheck} color="bg-emerald-500"
                />
                <FiletCard
                  label="RBAC"
                  enabled={filets.rbac?.enabled}
                  detail={`Rôles: ${(filets.rbac?.roles || []).join(', ')}`}
                  icon={Shield} color="bg-amber-500"
                />
                <FiletCard
                  label="Audit HTTP"
                  enabled={filets.audit_requests?.enabled}
                  detail={filets.audit_requests?.description || ''}
                  icon={ScrollText} color="bg-slate-500"
                />
                <FiletCard
                  label="Audit Actions"
                  enabled={filets.audit_actions?.enabled}
                  detail={`${filets.audit_actions?.total_logged_actions || 0} actions enregistrées`}
                  icon={ScrollText} color="bg-rose-500"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section: Alertes Coûts */}
        <Card>
          <CardHeader className="pb-2">
            <SectionHeader icon={DollarSign} label="Alertes Coûts" open={sections.alerts} onToggle={() => toggleSection('alerts')} />
          </CardHeader>
          {sections.alerts && (
            <CardContent className="space-y-4">
              {/* Config */}
              <div className="flex items-end gap-3 p-3 rounded-lg border bg-muted/30">
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Seuil par défaut (USD/mois)</label>
                  <Input
                    data-testid="threshold-input"
                    type="number" min="1" step="1"
                    value={threshold}
                    onChange={e => setThreshold(e.target.value)}
                    className="h-8 w-32"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="toggle-alerts-btn"
                    size="sm"
                    variant={alertsEnabled ? 'default' : 'outline'}
                    onClick={() => setAlertsEnabled(!alertsEnabled)}
                  >
                    {alertsEnabled ? 'Activé' : 'Désactivé'}
                  </Button>
                  <Button data-testid="save-alert-config-btn" size="sm" onClick={saveAlertConfig}>
                    <Settings className="w-3.5 h-3.5 mr-1" /> Sauvegarder
                  </Button>
                </div>
              </div>

              {/* Alerts table */}
              {alerts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead className="text-right">Coût ({costAlerts?.month})</TableHead>
                      <TableHead className="text-right">Seuil</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map(a => (
                      <TableRow key={a.tenant_id} data-testid={`alert-row-${a.tenant_id}`}>
                        <TableCell className="font-medium text-sm">{a.tenant_name}</TableCell>
                        <TableCell className="text-right text-sm">${a.cost_usd.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm">${a.threshold_usd}</TableCell>
                        <TableCell>
                          {a.exceeded
                            ? <Badge variant="destructive" className="text-[11px]">Dépassé</Badge>
                            : <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">OK</Badge>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Aucun tenant avec usage ce mois-ci</p>
              )}
            </CardContent>
          )}
        </Card>

        {/* Section: Santé Tenants */}
        <Card>
          <CardHeader className="pb-2">
            <SectionHeader icon={HeartPulse} label={`Santé Tenants (${tenantHealth?.tenants_checked || 0} vérifiés)`} open={sections.health} onToggle={() => toggleSection('health')} />
          </CardHeader>
          {sections.health && (
            <CardContent>
              {healthIssues.length === 0 ? (
                <div className="text-center py-6">
                  <HeartPulse className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Tous les tenants sont en bonne santé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {healthIssues.map(t => (
                    <div key={t.tenant_id} data-testid={`health-issue-${t.tenant_id}`} className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800">
                      <p className="text-sm font-medium mb-1">{t.tenant_name}</p>
                      <ul className="space-y-0.5">
                        {t.issues.map((issue, i) => (
                          <li key={i} className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" /> {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Section: Audit Log */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <SectionHeader icon={ScrollText} label={`Audit Log (${auditLog?.total || 0} actions)`} open={sections.audit} onToggle={() => toggleSection('audit')} />
              <Button data-testid="refresh-security-btn" variant="ghost" size="sm" onClick={fetchAll} className="h-7">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardHeader>
          {sections.audit && (
            <CardContent>
              {actions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune action enregistrée</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Acteur</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Cible</TableHead>
                      <TableHead>Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actions.map((a, i) => (
                      <TableRow key={i} data-testid={`audit-row-${i}`}>
                        <TableCell className="text-xs">{formatDate(a.timestamp)}</TableCell>
                        <TableCell className="text-xs">{a.actor_email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[11px]">{a.action}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{a.target_type}{a.target_id ? `: ${a.target_id}` : ''}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-48 truncate">
                          {JSON.stringify(a.details).slice(0, 80)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>

      </div>
    </PlatformLayout>
  );
}
